import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Box,
  MenuItem,
  InputAdornment,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  PhoneInTalk,
  PhoneMissed,
  Search as SearchIcon,
  ExpandLess,
  ExpandMore,
  CalendarToday,
} from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { Skeleton } from "@mui/material";
import { Pagination } from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";

const statusLabels = {
  connected: "Answered",
  "not connected": "Missed",
};

const outcomeLabels = {
  successful: "Successful",
  missed: "Missed",
  follow_up_needed: "Follow-up Needed",
  not_interested: "Not Interested",
  callback_requested: "Callback Requested",
};

const statusColors = {
  connected: "success",
  "not connected": "error",
};

const infoLabels = {
  connected: "Connected",
  agent_missed: "Agent Missed",
  customer_missed: "Customer Missed",
};

const infoColors = {
  connected: "success",
  agent_missed: "error",
  customer_missed: "error",
};

const CallLogItem = ({ call, contact }) => {
  const [open, setOpen] = useState(false);

  const toggleCollapse = () => setOpen(!open);

  const normalizedCallStatus = (() => {
    // const agentStatus = call.agent_status?.toLowerCase();
    const customerStatus = call.customer_status?.toLowerCase();
    const baseStatus = call.status?.toLowerCase();
    if (baseStatus === "connected" && customerStatus === "customer_missed") {
      return "not connected";
    }
    return baseStatus;
  })();

  const chip = (
    <Chip
      size="small"
      label={statusLabels[normalizedCallStatus]}
      color={statusColors[normalizedCallStatus] || "default"}
    />
  );

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Box display="flex" gap={2} alignItems="center">
          {call.status?.toLowerCase() === "connected" ? (
            <PhoneInTalk color="success" />
          ) : (
            <PhoneMissed color="error" />
          )}
          <Box>
            <Typography variant="subtitle1">
              {contact?.company || contact?.first_name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {format(new Date(call.start_time), "MMM d, h:mm a")}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {call.agent_status && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip
                slotProps={tooltipStyleProps}
                title={
                  infoLabels[call.agent_status?.toLowerCase()] ||
                  call.agent_status
                }
                arrow
              >
                <Chip
                  label="Agent"
                  size="small"
                  color={
                    infoColors[call.agent_status?.toLowerCase()] || "default"
                  }
                />
              </Tooltip>
            </Box>
          )}

          {/* Customer Info (only if agent connected) */}
          {call.agent_status?.toLowerCase() === "connected" &&
            call.customer_status && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Tooltip
                  slotProps={tooltipStyleProps}
                  title={
                    infoLabels[call.customer_status?.toLowerCase()] ||
                    call.customer_status
                  }
                  arrow
                >
                  <Chip
                    label="Customer"
                    size="small"
                    color={
                      infoColors[call.customer_status?.toLowerCase()] ||
                      "default"
                    }
                  />
                </Tooltip>
              </Box>
            )}
          {call.disconnected_by ? (
            <Tooltip
              slotProps={tooltipStyleProps}
              title={`Disconnected by - ${call.disconnected_by}`}
              arrow
            >
              {chip}
            </Tooltip>
          ) : (
            chip
          )}

          <IconButton size="small" onClick={toggleCollapse}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={open}>
        <Divider />
        <Box p={2}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", color: "textSecondary" }}
              >
                Outcome :{" "}
              </Typography>
              <Chip
                size="small"
                label={outcomeLabels[call.outcome]}
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="textSecondary">
                Phone Number
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {call.phone_number}
              </Typography>
            </Box>
          </Box>

          {call.notes && (
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Notes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#f9f9f9",
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {call.notes}
              </Typography>
            </Box>
          )}

          {call.outcome === "callback_requested" && call.follow_up_date && (
            <Box
              mt={2}
              display="flex"
              alignItems="center"
              gap={1}
              bgcolor="#f3e8ff"
              p={1}
              borderRadius={1}
            >
              <CalendarToday fontSize="small" color="secondary" />
              <Typography variant="body2" color="secondary">
                Callback scheduled:{" "}
                {format(new Date(call.follow_up_date), "MMM d, h:mm a")}
              </Typography>
            </Box>
          )}

          {call.recording_url &&
            call.status?.toLowerCase() !== "not connected" &&
            (call.customer_status?.toLowerCase() !== "customer_missed" ||
              call.agent_status?.toLowerCase() !== "agent_missed") && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  Recording
                </Typography>
                <audio controls style={{ width: "100%", marginTop: 4 }}>
                  <source src={call.recording_url} type="audio/mpeg" />
                </audio>
              </Box>
            )}
        </Box>
      </Collapse>
    </Card>
  );
};

const CallLogSkeleton = () => (
  <>
    {[...Array(3)].map((_, i) => (
      <Card variant="outlined" sx={{ mb: 2 }} key={i}>
        <Box display="flex" justifyContent="space-between" p={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Skeleton variant="circular" width={32} height={32} />
            <Box>
              <Skeleton variant="text" width={120} height={20} />
              <Skeleton variant="text" width={80} height={14} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={60} height={24} />
        </Box>
        <Divider />
        <Box p={2}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
        </Box>
      </Card>
    ))}
  </>
);

export default function CallLogsList({
  selectedFromDate,
  selectedToDate,
  selectedAgent,
  selectedContact,
  contactsMap,
}) {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
  };

  const loadCalls = React.useCallback(async () => {
    setIsLoading(true);
    try {
      let params = {};

      const effectiveFrom = selectedFromDate || new Date();
      const effectiveTo = selectedToDate || new Date();

      params.start_date = formatDate(effectiveFrom);
      params.end_date = formatDate(effectiveTo);

      if (selectedAgent && selectedAgent !== "all") {
        params.assigned_to = selectedAgent;
      }

      if (selectedContact && selectedContact !== "all") {
        params.contact_id = selectedContact;
      }

      console.log("Loading calls with params", params);

      const response = await axios.get(`${domain}crm/calls/`, {
        params,
        headers: {
          Authorization: `token ${token}`,
        },
      });
      console.log("API response for calls:", response);
      console.log(response.data);
      setCalls(response.data);
    } catch (error) {
      console.error("Failed to load call logs", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedFromDate,
    selectedToDate,
    selectedAgent,
    selectedContact,
    domain,
    token,
  ]);

  useEffect(() => {
    loadCalls();
  }, [
    selectedFromDate,
    selectedToDate,
    selectedContact,
    selectedAgent,
    loadCalls,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedFromDate,
    selectedToDate,
    selectedAgent,
    selectedContact,
    searchTerm,
    statusFilter,
    outcomeFilter,
  ]);

  function formatDate(date) {
    const d = typeof date === "string" ? new Date(date) : date;
    if (!(d instanceof Date) || isNaN(d)) {
      console.error("Invalid date passed to formatDate:", date);
      return null;
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  console.log("calls", calls);

  const filteredCalls = calls?.filter((call) => {
    const contact = contactsMap?.[call.contact] || {};
    const searchMatch =
      !searchTerm ||
      contact?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.phone_number?.includes(searchTerm) ||
      call.notes?.toLowerCase().includes(searchTerm);

    const statusMatch =
      statusFilter === "all" || call.status?.toLowerCase() === statusFilter;
    const outcomeMatch =
      outcomeFilter === "all" || call.outcome?.toLowerCase() === outcomeFilter;

    return searchMatch && statusMatch && outcomeMatch;
  });

  const paginatedCalls = filteredCalls?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil((filteredCalls?.length || 0) / itemsPerPage);

  console.log("filteredCalls", filteredCalls, "calls", calls);

  return (
    <Card
      variant="outlined"
      // sx={{ mt: 3 }}
      sx={{
        borderTop: "4px solid #84DEB8",
        borderRadius: 2,
        boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        title={`Call Logs (${filteredCalls?.length})`}
        subheader="Filter and review past call activity"
      />
      <CardContent>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField
            label="Search"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Status"
            size="small"
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            {Object.entries(statusLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Outcome"
            size="small"
            select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All Outcomes</MenuItem>
            {Object.entries(outcomeLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {isLoading ? (
          <CallLogSkeleton />
        ) : filteredCalls?.length > 0 ? (
          <>
            {paginatedCalls.map((call) => (
              <CallLogItem
                key={call.id}
                call={call}
                contact={contactsMap[call.contact]}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  size="small"
                  shape="rounded"
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <>
            <Box my={4} />
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "textSecondary" }}
              textAlign="center"
            >
              No calls found for the selected criteria.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
